/**
 * Snakes and Ladders Dice Component
 *
 * Renders a die with the current value (1–6 dots).
 * Rolling animation before final value (within 1 second).
 * Click/touch to trigger roll; disabled when not player's turn.
 */

import React, { useState, useEffect, useCallback } from 'react';
import './SnakesLaddersDice.css';

interface SnakesLaddersDiceProps {
  value: number | null;
  rolling: boolean;
  disabled: boolean;
  onRoll: () => void;
  playerColor?: string;
}

const DOT_POSITIONS: Record<number, number[][]> = {
  1: [[50, 50]],
  2: [[25, 25], [75, 75]],
  3: [[25, 25], [50, 50], [75, 75]],
  4: [[25, 25], [75, 25], [25, 75], [75, 75]],
  5: [[25, 25], [75, 25], [50, 50], [25, 75], [75, 75]],
  6: [[25, 25], [75, 25], [25, 50], [75, 50], [25, 75], [75, 75]],
};

const SnakesLaddersDice: React.FC<SnakesLaddersDiceProps> = ({
  value,
  rolling,
  disabled,
  onRoll,
  playerColor,
}) => {
  const [displayValue, setDisplayValue] = useState<number>(value || 1);
  const [isAnimating, setIsAnimating] = useState(false);

  // Animate dice rolling
  useEffect(() => {
    if (!rolling) {
      if (value) setDisplayValue(value);
      setIsAnimating(false);
      return;
    }

    setIsAnimating(true);
    let frame = 0;
    const totalFrames = 10;
    const interval = setInterval(() => {
      setDisplayValue(Math.floor(Math.random() * 6) + 1);
      frame++;
      if (frame >= totalFrames) {
        clearInterval(interval);
        if (value) setDisplayValue(value);
        setIsAnimating(false);
      }
    }, 80);

    return () => clearInterval(interval);
  }, [rolling, value]);

  const handleClick = useCallback(() => {
    if (!disabled && !rolling) {
      onRoll();
    }
  }, [disabled, rolling, onRoll]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled && !rolling) {
        e.preventDefault();
        onRoll();
      }
    },
    [disabled, rolling, onRoll]
  );

  const dots = DOT_POSITIONS[displayValue] || DOT_POSITIONS[1];

  return (
    <div className="sl-dice-container">
      <button
        className={`sl-dice ${isAnimating ? 'sl-dice--rolling' : ''} ${disabled ? 'sl-dice--disabled' : ''}`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={`Dice showing ${displayValue}. ${disabled ? 'Not your turn.' : 'Click to roll.'}`}
        aria-live="polite"
        role="button"
        tabIndex={0}
        style={playerColor ? { borderColor: playerColor } as React.CSSProperties : undefined}
      >
        <svg viewBox="0 0 100 100" className="sl-dice__face" aria-hidden="true">
          {dots.map(([cx, cy], i) => (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r="10"
              className="sl-dice__dot"
            />
          ))}
        </svg>
      </button>
      {!disabled && !rolling && (
        <p className="sl-dice__hint" aria-hidden="true">
          Tap to roll
        </p>
      )}
      {rolling && (
        <p className="sl-dice__hint sl-dice__hint--rolling" aria-live="assertive">
          Rolling...
        </p>
      )}
    </div>
  );
};

export { SnakesLaddersDice };
export default SnakesLaddersDice;
